/**
 * MyPage「DLE評価」タブ本体 (個人 vs 全体 DLE 平均)。
 *
 * デザイナーのスキル評価タブ (`DesignerSkillEvaluationTab`) と同じ作りで、radar と
 * 推移折線を切替え、月 / 四半期の粒度と遡る期間を選べる。違いは軸が 4 本 (= 菱形) で
 * 1 枚だけという点と、系列色が紫 (個人) / 淡紫 (個人の前期間) / 灰 (全体 DLE 平均)
 * に固定されている点。
 *
 * **閲覧権限 (`canViewOthers`) があれば対象 DLE を切り替えられる。** 管理者 /
 * group_manager / team_leader / PMOT 所属が対象で、本人が DLE でなくてもタブが出る。
 * 権限が無ければ切替は出ず、自分のぶんだけを読む。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 個人の評価 4 軸を全体 DLE 平均と比較する MyPage タブ (radar / 推移, 月 / Q)
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useDleScores, useMyDleScores } from '../../../hooks/useDleEvaluationSurvey.js';
import { EvalRadar } from '../idt-kpi/DesignEvaluationRadar.js';
import { DesignEvalSelect } from '../idt-kpi/DesignEvalSelect.js';
import { TrendViewSwitch } from '../idt-kpi/TrendViewSwitch.js';
import { EvalModeSwitch, RadarLegend, type EvalMode } from '../idt-kpi/EvalChartControls.js';
import { RADAR_PREV_COLOR } from '../idt-kpi/designEvaluation.js';
import { AxisTrendColumn, type SwitchableAxis } from '../idt-mypage/DesignerEvalAxisTrend.js';
import type { TrendView } from '../idt-kpi/shared.js';
import type { MyDleScoresResponse } from '../../../types/dle-evaluation-score.js';
import {
  DLE_RADAR_SELF_COLOR,
  DLE_RADAR_SELF_PREV_COLOR,
  DLE_RADIUS_DOMAIN,
  DLE_SCORE_AXES,
  DLE_TREND_DOMAIN,
  type DleScorePoint,
} from '../../../types/dle-evaluation-score.js';
import { DleCommentColumn } from './DleCommentQuadrants.js';
import {
  clipDleMonthsToData,
  dleFyLabel,
  dleFyMonths,
  dleFyStartYearOf,
  dleMonthLabel,
  dleMonthShortLabel,
} from './dle-eval-periods.js';

/** 推移モードの軸スイッチ項目 (4 軸)。 @graph-connects none */
const DLE_SWITCH_AXES: SwitchableAxis[] = DLE_SCORE_AXES.map((axis) => ({
  key: axis.key,
  color: axis.color,
  labelKey: axis.labelKey,
  domain: DLE_TREND_DOMAIN,
}));

/** その期間に 1 つでも評点があるか (評価が付いていない月を既定の選択から外す)。 @graph-connects none */
function hasAnyScore(point: DleScorePoint): boolean {
  return Object.values(point.scores).some((value) => value != null);
}

/**
 * 4 軸の平均点 + 総合スコアを radar 下に横 1 列で表示する。
 * radar は形で傾向を示すだけなので、実数はここで読めるようにする。
 * @graph-connects none
 */
function DleScoreStrip({ point }: { point: DleScorePoint }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-end justify-center gap-x-4 gap-y-1">
      {DLE_SCORE_AXES.map((axis) => (
        <div key={axis.key} className="flex flex-col items-center gap-0.5 leading-none">
          <span className="text-[10px] text-[var(--color-text-secondary)]">{t(axis.labelKey)}</span>
          <span
            className="font-mono text-sm font-bold tabular-nums"
            style={{ color: DLE_RADAR_SELF_COLOR }}
          >
            {point.scores[axis.key]?.toFixed(2) ?? '—'}
          </span>
        </div>
      ))}
      <div className="flex flex-col items-center gap-0.5 leading-none">
        <span className="text-[10px] text-[var(--color-text-muted)]">
          {t('dleEvaluation.overallScore')}
        </span>
        <span className="font-mono text-sm font-bold tabular-nums text-[var(--color-text-primary)]">
          {point.overall?.toFixed(2) ?? '—'}
        </span>
      </div>
    </div>
  );
}

/**
 * radar モード: 4 軸の菱形 1 枚 (個人 / 個人の前期間 / 全体 DLE 平均) と、
 * それを囲む設問別コメントの 4 象限。
 *
 * **左上=一次回答 / 左下=壁打ち / 右上=返信速度 / 右下=依頼意向**。幅が足りないときは
 * radar を先頭にして 1 列へ積む (コメントが先に来ると radar が画面外に押し出される)。
 * @graph-connects DleCommentColumn [calls] 設問別コメント 2 件ぶんの縦並び
 */
function DleRadarView({
  selected,
  overall,
  selectedPrev,
}: {
  selected: DleScorePoint | null;
  overall: DleScorePoint | null;
  selectedPrev: DleScorePoint | null;
}) {
  const { t } = useTranslation();
  const selfLabel = t('dleEvaluation.self');
  const selfPrevLabel = t('dleEvaluation.selfPrev');
  const overallLabel = t('dleEvaluation.overall');

  if (!selected || !hasAnyScore(selected)) {
    return (
      <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('dleEvaluation.noSelf')}
      </div>
    );
  }

  return (
    // @container でカード実幅を基準に切り替える。viewport の lg だとサイドバー展開で
    // カードが縮んでも 3 列を維持し、radar の描画域が潰れる。
    <div className="@container">
      <div className="grid grid-cols-1 gap-x-5 gap-y-6 @[1100px]:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)]">
        {/* 左: 設問 1 上 / 設問 2 下 */}
        <div className="order-2 @[1100px]:order-1 @[1100px]:py-8">
          <DleCommentColumn topAxisIndex={0} bottomAxisIndex={1} point={selected} />
        </div>
        {/* 中央: radar + 点数ストリップ + 凡例 */}
        <div className="order-1 flex flex-col items-center gap-2 @[1100px]:order-2">
          <EvalRadar
            axes={DLE_SCORE_AXES}
            selected={selected.scores}
            prev={overall?.scores ?? null}
            extra={selectedPrev?.scores ?? null}
            extraColor={DLE_RADAR_SELF_PREV_COLOR}
            extraLabel={selectedPrev ? selfPrevLabel : null}
            height={560}
            domain={DLE_RADIUS_DOMAIN}
            currentColor={DLE_RADAR_SELF_COLOR}
            selectedLabel={selfLabel}
            prevLabel={overall ? overallLabel : null}
            axisCounts={selected.counts}
            axisLabelColor="var(--color-text-primary)"
            showDiff
            diffPositiveColor={DLE_RADAR_SELF_COLOR}
          />
          <DleScoreStrip point={selected} />
          <RadarLegend
            items={[
              { color: DLE_RADAR_SELF_COLOR, label: selfLabel },
              ...(selectedPrev ? [{ color: DLE_RADAR_SELF_PREV_COLOR, label: selfPrevLabel }] : []),
              { color: RADAR_PREV_COLOR, label: overallLabel },
            ]}
          />
        </div>
        {/* 右: 設問 3 上 / 設問 4 下 */}
        <div className="order-3 @[1100px]:py-8">
          <DleCommentColumn topAxisIndex={2} bottomAxisIndex={3} point={selected} />
        </div>
      </div>
    </div>
  );
}

/**
 * DLE 評価の表示本体（radar / 推移、月 / Q、期間の遡り、設問別コメント）。
 *
 * **データの取得はしない。** 誰の評価を出すかは呼び出し側が決める — MyPage は自分 or 切替で
 * 選んだ相手、メンバー詳細ページはそのページのメンバー。取得まで抱えると「どの画面から
 * 来たか」で分岐が増えるので、描画だけを持つ。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 個人の評価 4 軸 (一次回答 / 壁打ち / 返信速度 / 依頼意向) を全体 DLE 平均と radar・推移で比較表示する
 * @graph-connects DleCommentColumn [calls] 設問別コメントの描画
 */
export function DleEvaluationPanel({
  data,
  isLoading,
  targetSelect,
  targetLabel,
}: {
  data: MyDleScoresResponse | undefined;
  isLoading: boolean;
  /** 対象 DLE の切替 UI。出さない画面は省略する */
  targetSelect?: ReactNode;
  /** 見出しに添える対象名。自分のぶんだけを見る画面では null */
  targetLabel?: string | null;
}) {
  const { t } = useTranslation();

  const [mode, setMode] = useState<EvalMode>('radar');
  const [granularity, setGranularity] = useState<TrendView>('month');
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [selectedFy, setSelectedFy] = useState<number | null>(null);

  const isQuarter = granularity === 'quarter';
  const selfMonthly = data?.monthly ?? [];
  const selfActive = (isQuarter ? data?.quarterly : data?.monthly) ?? [];
  const overallActive = (isQuarter ? data?.overallQuarterly : data?.overallMonthly) ?? [];
  const overallMonthly = data?.overallMonthly ?? [];

  const selfByPeriod = useMemo(
    () => new Map(selfActive.map((point) => [point.period, point])),
    [selfActive],
  );
  const overallByPeriod = useMemo(
    () => new Map(overallActive.map((point) => [point.period, point])),
    [overallActive],
  );
  // 推移折線は軸キー → 値 のレコードだけを見る (radar と同じ汎用コンポーネントを共有する)。
  const selfAxisValues = useMemo(
    () => new Map(selfActive.map((point) => [point.period, point.scores])),
    [selfActive],
  );
  const overallAxisValues = useMemo(
    () => new Map(overallActive.map((point) => [point.period, point.scores])),
    [overallActive],
  );

  // radar の既定選択は「評点が付いている最新期間」。当月は回答が集まる前なので空になりやすい。
  const latestSelfPeriod = useMemo(() => {
    for (let i = selfActive.length - 1; i >= 0; i--) {
      const point = selfActive[i];
      if (point && hasAnyScore(point)) return point.period;
    }
    return selfActive[selfActive.length - 1]?.period ?? null;
  }, [selfActive]);
  const selectedPeriod = (isQuarter ? selectedQuarter : selectedMonth) ?? latestSelfPeriod;
  const selectedSelf = selectedPeriod != null ? (selfByPeriod.get(selectedPeriod) ?? null) : null;
  const selectedOverall =
    selectedPeriod != null ? (overallByPeriod.get(selectedPeriod) ?? null) : null;

  // 参照線は「選択期間より前で評点がある直近の自分の期間」。評価が無い月を挟んでも
  // 前回との比較が切れないようにする。
  const selectedSelfPrev = useMemo(() => {
    if (selectedPeriod == null) return null;
    const index = selfActive.findIndex((point) => point.period === selectedPeriod);
    if (index <= 0) return null;
    for (let i = index - 1; i >= 0; i--) {
      const point = selfActive[i];
      if (point && hasAnyScore(point)) return point;
    }
    return null;
  }, [selfActive, selectedPeriod]);

  // 推移の x 軸: 月は会計年度の 12 ヶ月をデータのある範囲へ切り詰めたもの、
  // Q は全体平均が持つ全 Q。評価が付かなかった月は「線が切れている」ことが読めるよう
  // 範囲内なら空のまま残す (切り詰めるのは端だけ)。
  const firstDataMonth = overallMonthly[0]?.period ?? selfMonthly[0]?.period ?? null;
  const latestMonth =
    overallMonthly[overallMonthly.length - 1]?.period ??
    selfMonthly[selfMonthly.length - 1]?.period ??
    null;
  const effectiveFy = selectedFy ?? (latestMonth != null ? dleFyStartYearOf(latestMonth) : null);
  const linePeriods = useMemo(
    () =>
      isQuarter
        ? (data?.overallQuarterly ?? []).map((point) => point.period)
        : effectiveFy != null
          ? clipDleMonthsToData(dleFyMonths(effectiveFy), firstDataMonth, latestMonth)
          : [],
    [isQuarter, data, effectiveFy, firstDataMonth, latestMonth],
  );

  const fullLabelOf = useMemo(
    () => (period: string) => (isQuarter ? period : dleMonthLabel(period)),
    [isQuarter],
  );
  const shortLabelOf = useMemo(
    () => (period: string) => (isQuarter ? period : dleMonthShortLabel(period)),
    [isQuarter],
  );

  // 遡れる期間の候補。radar は自分に評価がある期間、推移 (月) は会計年度。
  const radarOptions = useMemo(
    () =>
      [...selfActive]
        .reverse()
        .map((point) => ({ value: point.period, label: fullLabelOf(point.period) })),
    [selfActive, fullLabelOf],
  );
  const fyOptions = useMemo(() => {
    const years = new Set<number>();
    for (const point of selfMonthly) years.add(dleFyStartYearOf(point.period));
    for (const point of overallMonthly) years.add(dleFyStartYearOf(point.period));
    return [...years]
      .filter((year) => year > 0)
      .sort((a, b) => b - a)
      .map((year) => ({ value: String(year), label: dleFyLabel(year) }));
  }, [selfMonthly, overallMonthly]);

  let periodSelect = null;
  if (mode === 'radar' && selectedPeriod != null) {
    periodSelect = (
      <DesignEvalSelect
        options={radarOptions}
        value={selectedPeriod}
        onChange={isQuarter ? setSelectedQuarter : setSelectedMonth}
        ariaLabel={t('dleEvaluation.periodSelectLabel')}
      />
    );
  } else if (mode === 'line' && !isQuarter && effectiveFy != null) {
    periodSelect = (
      <DesignEvalSelect
        options={fyOptions}
        value={String(effectiveFy)}
        onChange={(value) => {
          setSelectedFy(parseInt(value, 10));
        }}
        ariaLabel={t('dleEvaluation.periodSelectLabel')}
      />
    );
  }

  const [trendAxisKey, setTrendAxisKey] = useState<string>(DLE_SWITCH_AXES[0]?.key ?? '');
  const hasAny = selfActive.length > 0 || overallActive.length > 0;

  return (
    <Card className="animate-content-reveal">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('dleEvaluation.title')}
            {/* 誰の評価を見ているかを常に出す。切替があると自分のぶんと取り違えやすい。 */}
            {targetLabel != null && (
              <span className="ml-2 text-[var(--color-text-muted)]">· {targetLabel}</span>
            )}
          </h3>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            {t('dleEvaluation.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {targetSelect}
          {periodSelect}
          <TrendViewSwitch view={granularity} onChange={setGranularity} />
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">Loading…</div>
      ) : !hasAny ? (
        <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
          {t('dleEvaluation.empty')}
        </div>
      ) : mode === 'radar' ? (
        <DleRadarView
          selected={selectedSelf}
          overall={selectedOverall}
          selectedPrev={selectedSelfPrev}
        />
      ) : (
        <AxisTrendColumn
          title={t('dleEvaluation.axesTitle')}
          axes={DLE_SWITCH_AXES}
          selectedKey={trendAxisKey}
          onSelect={setTrendAxisKey}
          selfByPeriod={selfAxisValues}
          overallByPeriod={overallAxisValues}
          periods={linePeriods}
          shortLabelOf={shortLabelOf}
          fullLabelOf={fullLabelOf}
          selfLabel={t('dleEvaluation.self')}
          overallLabel={t('dleEvaluation.overall')}
        />
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-[var(--color-text-muted)]">
          {t('dleEvaluation.scaleNote')}
          {selectedSelf && mode === 'radar' && (
            <>
              {' · '}
              {t('dleEvaluation.answerCount', { count: selectedSelf.answerCount })}
            </>
          )}
          {data != null && (
            <>
              {' · '}
              {t('dleEvaluation.overallDleCount', { count: data.overallDleCount })}
            </>
          )}
        </p>
        <EvalModeSwitch
          mode={mode}
          onChange={setMode}
          radarLabel={t('dleEvaluation.modeRadar')}
          lineLabel={t('dleEvaluation.modeLine')}
          ariaLabel={t('dleEvaluation.modeSwitchLabel')}
        />
      </div>
    </Card>
  );
}

/**
 * MyPage「DLE評価」タブ。自分の評価を出し、閲覧権限があれば対象 DLE を切り替えられる。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business MyPage の DLE 評価タブ。自分の評価を表示し、権限があれば対象 DLE を切り替える
 * @graph-connects useMyDleScores [calls] 自分の DLE 評価集計取得
 * @graph-connects useDleScores [calls] 切替で選んだ DLE の評価集計取得（閲覧権限必須）
 * @graph-connects DleEvaluationPanel [calls] 評価の表示本体
 */
export function DleEvaluationTab() {
  const { t } = useTranslation();
  const base = useMyDleScores();
  const canViewOthers = base.data?.canViewOthers ?? false;
  const dles = base.data?.dles ?? [];
  const [selectedDle, setSelectedDle] = useState<string | null>(null);

  // 閲覧権限があるときは、自分を見る場合も含めて常に /scores を通す。自分かどうかで
  // データ源を変えると「自分だけ挙動が違う」バグの温床になる。
  // 既定の対象は、自分が DLE なら自分、そうでなければ一覧の先頭。
  const defaultTarget = base.data?.isDle === true ? base.data.dle : (dles[0] ?? null);
  const target = selectedDle ?? defaultTarget;
  const other = useDleScores(canViewOthers ? target : null);

  return (
    <DleEvaluationPanel
      data={canViewOthers ? other.data : base.data}
      isLoading={canViewOthers ? base.isLoading || other.isLoading : base.isLoading}
      targetLabel={canViewOthers ? target : null}
      targetSelect={
        canViewOthers && dles.length > 0 && target != null ? (
          <DesignEvalSelect
            options={dles.map((name) => ({ value: name, label: name }))}
            value={target}
            onChange={setSelectedDle}
            ariaLabel={t('dleEvaluation.targetSelectLabel')}
          />
        ) : undefined
      }
    />
  );
}
